import React, { useState } from 'react'

// Shows a member's tournament participation (dates / year) and, for players,
// their best medal and the year it was won. Used in the Player and Official
// detail views in the admin panel.
const MEDALS = {
  gold:   { bg: '#fef9c3', border: '#facc15', color: '#854d0e', label: 'Gold' },
  silver: { bg: '#f1f5f9', border: '#cbd5e1', color: '#475569', label: 'Silver' },
  bronze: { bg: '#fdf2e9', border: '#e8b98a', color: '#9a3412', label: 'Bronze' },
}

const formatDate = (d) => {
  if (!d) return '-'
  const date = new Date(String(d).length <= 10 ? d + 'T00:00:00' : d)
  if (isNaN(date.getTime())) return d
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const MemberParticipation = ({ data, loading }) => {
  const isPlayer = data?.is_player
  const rows = data?.participation || []
  // Group the player's tournaments by medal so all three (Gold / Silver / Bronze) can be shown.
  const medalOrder = ['gold', 'silver', 'bronze']
  const rowsByMedal = { gold: [], silver: [], bronze: [] }
  rows.forEach(r => { if (r.medal && rowsByMedal[r.medal]) rowsByMedal[r.medal].push(r) })
  const wonMedals = medalOrder.filter(m => rowsByMedal[m].length > 0)
  const [openMedals, setOpenMedals] = useState({})

  return (
    <div style={{
      marginTop: '1.5rem',
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb',
        background: 'linear-gradient(to right, #fff5f5, #ffffff)'
      }}>
        <i className="fas fa-trophy" style={{ color: '#c00f15' }}></i>
        <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1f2937' }}>Tournament Participation</h3>
        {!loading && data && !isPlayer && (
          <span style={{
            marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 600,
            padding: '3px 10px', borderRadius: '20px', background: '#fee2e2', color: '#991b1b'
          }}>
            {data.total_tournaments} tournament{data.total_tournaments === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div style={{ padding: '1.25rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem' }}>
            <i className="fas fa-spinner fa-spin"></i> Loading participation…
          </div>
        ) : !data || rows.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>
            <i className="fas fa-calendar-times" style={{ fontSize: '1.75rem', marginBottom: '0.5rem', display: 'block' }}></i>
            No tournament participation yet.
          </div>
        ) : (
          <>
            {/* Medals won (Gold / Silver / Bronze) — players only */}
            {isPlayer && (
              wonMedals.length > 0 ? (
                <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {wonMedals.map(mk => {
                    const M = MEDALS[mk]
                    const mRows = rowsByMedal[mk]
                    const isOpen = !!openMedals[mk]
                    return (
                      <div key={mk}>
                        <div
                          onClick={() => setOpenMedals(o => ({ ...o, [mk]: !o[mk] }))}
                          title="Click to view the tournament(s) where this medal was won"
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '0.75rem 1rem', borderRadius: isOpen ? '10px 10px 0 0' : '10px',
                            background: M.bg, border: `1px solid ${M.border}`,
                            cursor: 'pointer', userSelect: 'none'
                          }}
                        >
                          <i className="fas fa-medal" style={{ fontSize: '1.75rem', color: M.color }}></i>
                          <div>
                            <div style={{ fontSize: '1rem', fontWeight: 700, color: M.color }}>
                              {M.label} Medal
                            </div>
                            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: M.color, fontWeight: 700 }}>
                              {mRows.length} tournament{mRows.length === 1 ? '' : 's'}
                            </div>
                          </div>
                          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ marginLeft: 'auto', color: M.color, fontSize: '0.85rem' }}></i>
                        </div>

                        {isOpen && (
                          <div style={{
                            border: `1px solid ${M.border}`, borderTop: 'none',
                            borderRadius: '0 0 10px 10px', overflow: 'hidden'
                          }}>
                            {mRows.map((r, i) => (
                              <div key={i} style={{
                                padding: '0.75rem 1rem', background: '#ffffff',
                                borderTop: i === 0 ? 'none' : '1px solid #f1f2f4'
                              }}>
                                <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>
                                  {r.event_name || '-'}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: '0.82rem', color: '#4b5563' }}>
                                  <span><i className="fas fa-layer-group" style={{ marginRight: '6px', color: '#9ca3af' }}></i>{r.category || '-'}</span>
                                  <span><i className="fas fa-calendar-day" style={{ marginRight: '6px', color: '#9ca3af' }}></i>{formatDate(r.start_date)} – {formatDate(r.end_date)}</span>
                                  <span><i className="fas fa-clock" style={{ marginRight: '6px', color: '#9ca3af' }}></i>Year {r.year || '-'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div style={{
                  padding: '0.6rem 1rem', marginBottom: '1rem', borderRadius: '10px',
                  background: '#f9fafb', border: '1px solid #e5e7eb', color: '#6b7280', fontSize: '0.85rem'
                }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                  No medal won yet.
                </div>
              )
            )}

            {/* Full participation table — hidden for players (they show only their best achievement) */}
            {!isPlayer && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    <th style={th}>Tournament</th>
                    <th style={th}>Category</th>
                    <th style={th}>Dates</th>
                    <th style={{ ...th, textAlign: 'center' }}>Year</th>
                    {isPlayer && <th style={{ ...th, textAlign: 'center' }}>Medal</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const m = r.medal ? MEDALS[r.medal] : null
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #eef0f3' }}>
                        <td style={{ ...td, fontWeight: 600, color: '#1f2937' }}>{r.event_name || '-'}</td>
                        <td style={{ ...td, color: '#374151' }}>{r.category || '-'}</td>
                        <td style={{ ...td, color: '#4b5563' }}>{formatDate(r.start_date)} – {formatDate(r.end_date)}</td>
                        <td style={{ ...td, textAlign: 'center', color: '#374151', fontWeight: 600 }}>{r.year || '-'}</td>
                        {isPlayer && (
                          <td style={{ ...td, textAlign: 'center' }}>
                            {m ? (
                              <span style={{
                                fontSize: '0.72rem', fontWeight: 700, padding: '2px 10px', borderRadius: '20px',
                                background: m.bg, color: m.color, border: `1px solid ${m.border}`
                              }}>
                                {m.label}
                              </span>
                            ) : (
                              <span style={{ color: '#d1d5db' }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const th = { padding: '10px 12px', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#6b7280', fontWeight: 700, whiteSpace: 'nowrap' }
const td = { padding: '10px 12px', verticalAlign: 'middle' }

export default MemberParticipation
